import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core"
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight"
import { createBoard } from "actions/boards"
import ButtonRounded from "components/Common/ButtonRounded"
import App from "contexts/App"
import { Form, Formik } from "formik"
import { useRef } from "react"
import { useEffect, useMemo } from "react"
import { useState } from "react"
import { useContext } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import serialize from "store/serialize"
import { v4 as uuidv4 } from "uuid"
import * as Yup from "yup"

import Step1 from "./Components/Step1"
import Step2 from "./Components/Step2"
import Step3 from "./Components/Step3"
import Step4 from "./Components/Step4"
import Step5 from "./Components/Step5"
import useStyles from "./styles"

const schema = Yup.object().shape({
  name: Yup.string()
    .max(255, "Name must be less/equal than 255 characters")
    .required("Required"),
  summary: Yup.string()
    .max(2000, "Summary must be less/equal than 2000 characters")
    .required("Required"),
  term: Yup.string().required("Required"),
  appointing_authority: Yup.string().required("Required"),
  enabling_legislation: Yup.string()
    .max(500, "Enabling Legislation must be less/equal than 500 characters")
    .nullable(true),
  reappointment: Yup.string().required("Required"),
  app_type: Yup.string().required("Required"),
  board_url: Yup.string()
    .max(255, "Board url must be less/equal than 255 characters")
    .nullable(true),
  minutes_url: Yup.string()
    .max(255, "Meeting minutes url must be less/equal to 255 characters")
    .nullable(true),
  minimum_degree: Yup.string(),
  occupation_required: Yup.string().nullable(true),
  interest: Yup.array().min(1, "Select at least one"),
  all_expertise: Yup.boolean().nullable(true),
  expertise: Yup.array()
    .min(1, "Select at least one")
    .when("all_expertise", {
      is: true,
      then: Yup.array().min(0),
    }),
  all_experience: Yup.boolean().nullable(true),
  experience: Yup.array()
    .min(1, "Select at least one")
    .when("all_experience", {
      is: true,
      then: Yup.array().min(0),
    }),
  positions: Yup.array(),
  default_questions: Yup.array(),
  additional_questions: Yup.array(),
})

const AddBoard = () => {
  const classes = useStyles()
  const formRef = useRef()
  const queryClient = useQueryClient()
  const history = useHistory()

  const { dialogs, closeDialog, authUser, openSnackBar, isClerk } =
    useContext(App)
  const thisDialog = dialogs?.["addBoard"] || {}
  const { open = false, data = {} } = thisDialog
  let fromEntity = data?.from === "entity"
  let entityId = data?.entity?.entityId
  let entityInfo = data?.entity

  const [step, setStep] = useState(1)
  const [positionsArray, setPositionsArray] = useState([])
  const [tabValue, setTabValue] = useState(1)
  const [additionalQuestions, setAdditionalQuestions] = useState([])
  const [newQuestion, setNewQuestion] = useState("")
  const [defaultQuestions, setDefaultQuestions] = useState([1, 2, 3])
  const [loading, setLoading] = useState(false)

  const [areasOfInterest, setAreasOfInterest] = useState([])
  const [areasOfExpertise, setAreasOfExpertise] = useState([])
  const [areasOfExperience, setAreasOfExperience] = useState([])

  const createBoardMutation = useMutation(createBoard)

  const handleClose = () => {
    closeDialog("addBoard")
    setTimeout(() => {
      setStep(1)
      setAdditionalQuestions([])
      setNewQuestion("")
      setDefaultQuestions([1, 2, 3])
      setAreasOfInterest([])
      setAreasOfExpertise([])
      setAreasOfExperience([])
      setPositionsArray(
        Array.from({ length: 1 }, () => ({
          id: uuidv4(),
        }))
      )
    }, 100)
  }

  const handleChangeStep = (operation) => {
    setStep((oldStep) => {
      if (Number(oldStep) < 5) {
        return operation === "+" ? oldStep + 1 : oldStep - 1
      } else if (Number(oldStep) === 5 && operation === "-") {
        return 5 - 1
      }
    })
  }

  const handleSubmit = () => {
    var createdBoardId
    setLoading(true)
    let data = { ...formRef?.current?.values, entity_id: authUser?.entity_id }

    const occupationRequired = () => {
      if (data?.occupation_required === null) {
        return null
      } else if (data?.occupation_required === "n/a") {
        return null
      } else {
        return Boolean(data?.occupation_required)
      }
    }

    data.occupation_required = occupationRequired()
    if (fromEntity) {
      data.entity_id = entityId
    }
    createBoardMutation
      .mutateAsync(data)
      .then((res) => {
        //if board creation occurring as clerk inside clerk portal...
        if (authUser?.entity_id) {
          createdBoardId = res.board[0].id
          serialize("board", res.board[0])
            .then((serializedData) => {
              return queryClient.setQueryData(
                ["boardsData", authUser && isClerk],
                (oldState) => {
                  let serializedWithEntity = {
                    ...serializedData,
                    entity: authUser?.entity,
                    entity_type:
                      authUser?.entity_type.charAt(0).toUpperCase() +
                      authUser?.entity_type.slice(1),
                    state: authUser?.state,
                  }
                  let newData = [...oldState, serializedWithEntity]
                  return newData
                }
              )
            })
            .then(() => {
              history.push(`/boards/${createdBoardId}`)
            })
        }
        //if board creation occurring as super with an entity...
        if (fromEntity) {
          serialize("board", res.board[0]).then((serializedData) => {
            return queryClient.setQueryData(
              ["entityBoardsData", entityId],
              (oldState) => {
                let newData = [...oldState, serializedData]
                return newData
              }
            )
          })
        }
        setLoading(false)
        handleClose()
        openSnackBar({ message: "Board created" })
      })
      .catch((err) => {
        setLoading(false)
        handleClose()
        openSnackBar({ message: "Error creating board" })
      })
  }

  //create positions array with length 1 to start
  useEffect(() => {
    setPositionsArray(
      Array.from({ length: 1 }, () => ({
        id: uuidv4(),
      }))
    )
  }, [])

  const positionsLength = useMemo(() => {
    return positionsArray?.length
  }, [positionsArray])

  //if validation if it is needed
  const submitPositions = () => {
    let refs = positionsArray.map((x) => x.ref.current)
    let promises = refs.map((ref, i) => {
      return new Promise((resolve, reject) => {
        //fix the problem with term-expiration that the error is not changing
        // and needs to click twice
        if (ref?.values?.member?.currently_vacant === false) {
          if (
            ref?.values?.member?.first_name !== "" &&
            ref?.values?.member?.last_name !== "" &&
            ref?.values?.member?.email !== "" &&
            ref?.values?.member?.terms_served !== "" &&
            ref?.values?.member?.terms_served !== null &&
            ref?.values?.member?.term_expiration !== "" &&
            ref?.values?.member?.term_expiration !== null &&
            ref?.errors?.member &&
            Object.keys(ref?.errors?.member)?.length === 1 &&
            ref?.errors?.member?.term_expiration
          ) {
            ref.setErrors({})
            resolve(ref)
          } else {
            ref.submitForm().then(() => {
              if (ref.isValid === true) {
                resolve(ref)
              } else {
                reject(ref)
              }
            })
          }
        } else {
          ref.submitForm().then(() => {
            if (ref.isValid === true) {
              resolve(ref)
            } else {
              reject(ref)
            }
          })
        }
      })
    })

    return Promise.all(promises)
  }

  //add position data to formik
  const addPositionsData = () => {
    return new Promise((resolve, reject) => {
      let refs = positionsArray.map((x) => x?.ref?.current?.values)
      if (refs) {
        formRef.current.setFieldValue("positions", refs)
        resolve()
      } else {
        reject()
      }
    })
  }
  const steps = [
    <Step1 key={1} />,
    <Step2
      key={2}
      setAreasOfInterest={setAreasOfInterest}
      areasOfInterest={areasOfInterest}
      setAreasOfExpertise={setAreasOfExpertise}
      areasOfExpertise={areasOfExpertise}
      setAreasOfExperience={setAreasOfExperience}
      areasOfExperience={areasOfExperience}
    />,
    <Step3
      key={3}
      setPositionsArray={setPositionsArray}
      positionsArray={positionsArray}
    />,
    <Step4
      key={4}
      additionalQuestions={additionalQuestions}
      setAdditionalQuestions={setAdditionalQuestions}
      newQuestion={newQuestion}
      setNewQuestion={setNewQuestion}
      defaultQuestions={defaultQuestions}
      setDefaultQuestions={setDefaultQuestions}
    />,
    <Step5
      key={5}
      tabValue={tabValue}
      areasOfInterest={areasOfInterest}
      areasOfExpertise={areasOfExpertise}
      areasOfExperience={areasOfExperience}
      entityInfo={entityInfo}
    />,
  ]

  const stepsTitle = [
    "Board Details",
    "Qualifiers",
    `Current Positions (${positionsLength})`,
    "Additional Information",
    "Preview",
  ]

  const handleChangeTabs = (event, newValue) => {
    setTabValue(newValue)
  }

  const nexStepFunc = (setErrors, errors) => {
    if (
      step === 1 &&
      (formRef?.current?.values?.board_name === "" ||
        formRef?.current?.values?.summary === "" ||
        formRef?.current?.values?.term_length === "" ||
        formRef?.current?.values?.appointing_authority === "" ||
        formRef?.current?.values?.reappointment === "" ||
        formRef?.current?.values?.app_type === "")
    ) {
      formRef?.current?.setErrors({})
      formRef?.current?.handleSubmit()
    } else if (
      step === 2 &&
      (areasOfInterest?.length === 0 ||
        (areasOfExperience?.length === 0 &&
          !formRef?.current?.values?.all_experience) ||
        (areasOfExpertise?.length === 0 &&
          !formRef?.current?.values?.all_expertise))
    ) {
      formRef.current.handleSubmit()
    } else if (step === 3) {
      addPositionsData()
        .then(() => {
          submitPositions().then(() => {
            handleChangeStep("+")
            setErrors({})
          })
        })
        .catch((err) => console.log(err))
    } else {
      handleChangeStep("+")
      setErrors({})
    }
  }

  return (
    <Dialog open={open} classes={{ paperWidthSm: classes.paper }}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h2">{stepsTitle[step - 1]}</Typography>
          {step === 5 ? (
            <Tabs
              value={tabValue}
              onChange={handleChangeTabs}
              classes={{
                indicator: classes.indicator,
              }}
            >
              <Tab
                classes={{
                  root: classes.tab,
                  selected: classes.selected,
                }}
                label="Listing"
                value={1}
              />
              <Tab classes={{ root: classes.tab }} label="Embedded" value={2} />
            </Tabs>
          ) : null}
        </Box>
      </DialogTitle>
      <Formik
        initialValues={{
          name: "",
          summary: "",
          term: "",
          appointing_authority: "",
          enabling_legislation: "",
          reappointment: "",
          app_type: "",
          board_url: "",
          minutes_url: "",
          minimum_degree: "",
          occupation_required: "",
          interest: [],
          expertise: [],
          all_expertise: false,
          experience: [],
          all_experience: false,
          positions: [],
          default_questions: [1, 2, 3],
          additional_questions: [],
        }}
        validationSchema={schema}
        innerRef={formRef}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          setErrors,
        }) => (
          <Form>
            <DialogContent className={classes.contentContainer}>
              {steps[step - 1]}
            </DialogContent>

            <DialogActions className={classes.actionsContainer}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Box display="flex" alignItems="center">
                  <ButtonRounded
                    variant="outlined"
                    color="primary"
                    className={classes.changeStepButton}
                    classes={{ outlined: classes.outlined }}
                    onClick={() => handleChangeStep("-")}
                    disabled={step === 1}
                  >
                    <KeyboardArrowLeftIcon style={{ fontSize: "12px" }} />
                  </ButtonRounded>
                  <Typography className={classes.stepsText} variant="h5">
                    {step}/5
                  </Typography>
                  <ButtonRounded
                    variant="outlined"
                    color="primary"
                    className={classes.changeStepButton}
                    classes={{ outlined: classes.outlined }}
                    onClick={() => {
                      nexStepFunc(setErrors, errors)
                    }}
                    disabled={step === 5}
                  >
                    <KeyboardArrowRightIcon style={{ fontSize: "12px" }} />
                  </ButtonRounded>
                </Box>
                <Box>
                  <ButtonRounded
                    className={classes.cancelButton}
                    onClick={handleClose}
                  >
                    Cancel
                  </ButtonRounded>
                  {step === 5 ? (
                    <ButtonRounded
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress style={{ color: "#fff" }} size={20} />
                      ) : (
                        "Save"
                      )}
                    </ButtonRounded>
                  ) : (
                    <ButtonRounded
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        nexStepFunc(setErrors, errors)
                      }}
                    >
                      Next
                    </ButtonRounded>
                  )}
                </Box>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default AddBoard
